import { TextField, TextFieldProps } from '@mui/material'
import React, { useCallback, useMemo } from 'react'

import { ChangeEvent, useEffect, useState } from 'react'
import FormItem from './form-item/form-item'

export type FormItemNumericProps = Omit<TextFieldProps, 'onChange' | 'onBlur'> & {
  onFocus?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => void
  onChange?: (value: number | null) => void
  onBlur?: (value: number | null) => void
  fullWidth?: true
  value: number | null
  baseValue?: number // The value returned if the inserted text is NaN
  min?: number
  max?: number
  errorMessage?: string
  fieldLabel?: React.ReactNode
  fieldTooltip?: string | JSX.Element
  fieldIcon?: React.ReactNode
  maxWidth?: string
  onError?: (error?: 'LESS_THAN_MIN' | 'GREATER_THAN_MAX') => void
}

export default function FormItemNumericField(props: FormItemNumericProps) {
  const { value, onChange, onBlur, onFocus, fieldLabel, fieldTooltip, fieldIcon, errorMessage, min, max, baseValue, fullWidth, maxWidth, InputProps, onError, ...textFieldProps } = props

  const [localValue, setLocalValue] = useState<number | null>(value)
  const [localMax, setLocalMax] = useState<number>(1000000000)
  useEffect(() => {
    if (max !== undefined) {
      setLocalMax(max)
    }
  }, [max])

  const textFieldNumberValue = useMemo(() => {
    if (localValue === null || localValue === undefined) {
      return ''
    }

    if (min !== undefined && localValue < min) {
      return min.toString()
    }

    if (localValue > localMax) {
      return localMax.toString()
    }

    return localValue.toString()
  }, [localValue])

  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value)
    }
  }, [value])

  const textFieldNumberOnChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): number | null => {
      const parsedValue = parseInt(event.target.value)
      if (Number.isNaN(parsedValue)) {
        return baseValue ?? null
      } else {
        if (min !== undefined && parsedValue < min) {
          onError?.('LESS_THAN_MIN')
          return min
        } else if (parsedValue > localMax) {
          onError?.('GREATER_THAN_MAX')
          return localMax
        }
        return parsedValue
      }
    },
    [min, localMax, baseValue],
  )

  return (
    <FormItem fullWidth={fullWidth} fieldLabel={fieldLabel} fieldTooltip={fieldTooltip} fieldIcon={fieldIcon} errorMessage={errorMessage} style={{ maxWidth: maxWidth }}>
      <TextField
        {...textFieldProps}
        inputProps={{ min: min, max: localMax }}
        value={textFieldNumberValue}
        onChange={(event) => {
          const r = RegExp('^[0-9]*$', 'g')
          if (r.test(event.target.value)) {
            const updValue = textFieldNumberOnChange(event)
            setLocalValue(updValue)
            if (onChange) onChange(updValue)
          }
        }}
        onBlur={() => {
          if (onBlur) {
            onBlur(localValue)
          }
        }}
        error={textFieldProps.error || errorMessage !== undefined}
        onFocus={onFocus}
        InputProps={{
          ...InputProps,
        }}
      />
    </FormItem>
  )
}
